import { logError } from '../error-reporting/log';

export type CameraPermissionState = 'granted' | 'denied' | 'unavailable';

export interface CameraPermissionResult {
  state: CameraPermissionState;
  prompted: boolean;
}

let hasGrantedCamera = false;
let pendingRequest: Promise<CameraPermissionResult> | null = null;

async function queryCameraPermission(): Promise<PermissionState | undefined> {
  try {
    const status = await navigator.permissions.query({ name: 'camera' });
    return status.state;
  } catch {
    // Firefox throws on the name; WebView support varies — fall through to getUserMedia
    return undefined;
  }
}

async function requestViaGetUserMedia(): Promise<CameraPermissionResult> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach((track) => {
      track.stop();
    });
    hasGrantedCamera = true;
    return { state: 'granted', prompted: true };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      return { state: 'denied', prompted: true };
    }
    // Any other error (no camera, camera busy, insecure context) must not block
    // capture — return 'unavailable' so callers just open the picker without the gate.
    logError(error);
    return { state: 'unavailable', prompted: true };
  }
}

async function resolveCameraPermission(): Promise<CameraPermissionResult> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    // non-secure context or unsupported engine
    return { state: 'unavailable', prompted: false };
  }

  // permissions.query only reflects the SITE grant — a camera that's site-granted but
  // denied for the browser app at the OS level still reads 'granted' here. So only trust a
  // definite 'denied' to short-circuit; for granted/prompt/unknown, probe getUserMedia —
  // the only check that also sees the OS/app-level permission and surfaces its denial.
  const queried = await queryCameraPermission();
  if (queried === 'denied') {
    return { state: 'denied', prompted: false };
  }

  return requestViaGetUserMedia();
}

/**
 * Permission gate for `<input capture>` flows — checks or asks, never captures.
 * `prompted: true` means a getUserMedia request ran.
 * The getUserMedia ask also grants the native CAMERA the capture needs — Android via
 * the WebView's onPermissionRequest, iOS via WKWebView.
 */
export function ensureCameraPermission(): Promise<CameraPermissionResult> {
  if (hasGrantedCamera) {
    return Promise.resolve({ state: 'granted', prompted: false });
  }
  if (pendingRequest) {
    return pendingRequest;
  }
  pendingRequest = resolveCameraPermission().finally(() => {
    pendingRequest = null;
  });
  return pendingRequest;
}
